import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { StyledCustomerRiskAndCoverageSlideOut } from './CustomerRiskAndCoverageSlideOut.style';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as customerRiskAndCoverageActionCreators from '@makemydeal/dr-dash-store/actions/customerRiskAndCoverageActionCreators';
import * as complianceActionCreators from '@makemydeal/dr-dash-store/actions/complianceActionCreators';

import type { CustomerRiskAndCoverageTabsRef } from '../header/CustomerRiskAndCoverageTabs';
import { CustomerRiskAndCoverageTabs } from '../header/CustomerRiskAndCoverageTabs';
import { useGetDrawerWidth } from '../dealActivity/activities/hooks/useGetDrawerWidth';

type SlideOutMode = 'insurance' | 'compliance' | 'credit_bureau';

interface VehicleInsuranceComplianceSlideOutProps {
    mode: SlideOutMode;
}

const CustomerRiskAndCoverageSlideOut: FC<VehicleInsuranceComplianceSlideOutProps> = ({ mode }) => {
    const dispatch = useDispatch();
    const shopper = useSelector(offerReduxSelectors.getShopperInfo);
    const panelWidth = useGetDrawerWidth();
    const customerRiskAndCoverageTabsRef = useRef<CustomerRiskAndCoverageTabsRef>(null);
    const showSlideOut = useSelector(baseDealSelectors.getCustomerRiskAndCoverageTabSlideOutStatus);

    useEffect(() => {
        if (showSlideOut) {
            dispatch(complianceActionCreators.clearResendIdVerification());
            dispatch(customerRiskAndCoverageActionCreators.viewCustomerRiskAndCoverageSlideOut());
        }
    }, [showSlideOut, dispatch, mode]);

    const handleHide = () => {
        // If using tabs (deal screen experience), use the tabs ref to check insurance tab
        if (customerRiskAndCoverageTabsRef.current) {
            const handled = customerRiskAndCoverageTabsRef.current.cancelInsuranceTab();
            if (handled) {
                return; // Insurance tab handled it, don't close the slideout
            }
            // Not on insurance tab, fall through to close the slideout
        }

        // close the slide out based on feature toggles (for compliance/credit bureau tabs when not on insurance)
        dispatch(baseDealActionCreators.setCustomerRiskAndCoverageSlideOutStatus(false));
        dispatch(customerRiskAndCoverageActionCreators.closeCustomerRiskAndCoverageSlideOut());
    };

    const header = `${shopper.firstName}${shopper.middleName ? ` ${shopper.middleName}` : ''} ${shopper.lastName}`;

    return (
        <StyledCustomerRiskAndCoverageSlideOut
            data-testid="vehicle-insurance-compliance-slide-out"
            id="vehicle-insurance-compliance-slide-out"
            show={showSlideOut}
            position="right"
            panelWidth={panelWidth}
            onHide={handleHide}
            header={header}
        >
            <CustomerRiskAndCoverageTabs ref={customerRiskAndCoverageTabsRef} />
        </StyledCustomerRiskAndCoverageSlideOut>
    );
};

export default CustomerRiskAndCoverageSlideOut;
